/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */

'use client'

import { type PropsWithChildren } from 'react'
import { Box, Chip, Typography, Stack, type ChipProps } from '@mui/material'
import { useTranslations } from 'next-intl'
import { ActionButton, SectionWrapper } from '@common-components'
import { Icon } from '@media-components'
import { AiCredit } from '@components/common/AiCredit'
import { IntroSectionProps } from '@components/entities'
import { getStyles } from '@utils/styles'
import { usePricingContext } from 'src/context'
import defaultStyles from './styles'

type ChipColor = ChipProps['color']

export function PricingIntroSection({
  heading,
  description,
  bulletPointsData,
  actionButtonData,
  tagData,
  sectionWrapperProps = {
    showGlow: false,
    alternateBg: true,
  },
  csx,
  aiCreditData,
  leftViewChild,
}: PropsWithChildren<Partial<IntroSectionProps>>) {
  const styles = getStyles(defaultStyles, csx)

  const chipColorConfig = (): ChipColor => {
    if (sectionWrapperProps?.alternateBg) return 'secondary'
    return 'primary'
  }

  const { ref, showGlow, alternateBg, ...restSectionWrapperProps } =
    sectionWrapperProps || {}

  const { dynamicPricingUSDAnnual } = usePricingContext()

  const t = useTranslations('Pricing')

  return (
    <SectionWrapper
      alternateBg={alternateBg}
      flexCol={{ xs: '20px', md: '32px' }}
      showGlow={showGlow}
      {...restSectionWrapperProps}
    >
      <Box {...styles('outerWrapper')}>
        <Box {...styles('mainContent')}>
          <Box {...styles('leftContent')}>
            {tagData && Boolean(tagData.label) && (
              <Box>
                <Chip
                  label={
                    <Typography variant="body3.500">{tagData.label}</Typography>
                  }
                  color={chipColorConfig()}
                  variant="filled"
                  {...styles('chip', {
                    backgroundColor: '#ffffff10',
                    padding: '12px 0px !important',
                    ':hover': {
                      backgroundColor: showGlow
                        ? '#ffffff10 !important'
                        : 'unset',
                      color: showGlow ? 'common.white' : 'common.black',
                    },
                    border: showGlow ? 'unset' : '1px solid',
                    borderColor: showGlow ? 'unset' : `border.light`,
                    color: showGlow ? 'common.white' : 'common.black',
                  })}
                />
              </Box>
            )}
            {heading && (
              <Typography
                variant="h3.600"
                component="h1"
                color={
                  !showGlow || !sectionWrapperProps?.showGlow
                    ? 'text.primary'
                    : 'text.primaryDark'
                }
              >
                {dynamicPricingUSDAnnual?.plan_type !== 'ENTERPRISE' ? (
                  <>
                    Try our {dynamicPricingUSDAnnual?.plan_name ?? 'Growth'}{' '}
                    plan for
                    <Typography
                      variant="h3.600"
                      component="div"
                      color={
                        !showGlow || !sectionWrapperProps?.showGlow
                          ? 'text.primary'
                          : 'text.primaryDark'
                      }
                      sx={{ color: 'red' }}
                    >
                      {`${dynamicPricingUSDAnnual?.discount ?? 20}% off!`}
                    </Typography>
                  </>
                ) : (
                  t('introsection.enterpriseTitle')
                )}
              </Typography>
            )}

            {description && (
              <Typography
                variant="body1.500"
                color={
                  !showGlow || !sectionWrapperProps?.showGlow
                    ? 'text.primary'
                    : 'text.primaryDark'
                }
              >
                {description}
              </Typography>
            )}

            {actionButtonData && actionButtonData.length > 0 && (
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                gap={2.5}
                display={{ xs: 'none', sm: 'flex' }}
              >
                {actionButtonData.map((item, i) => (
                  <ActionButton
                    {...item}
                    key={i}
                    showArrow
                    iconSx={{ color: 'common.black' }}
                    {...styles('actionButton')}
                  />
                ))}
              </Stack>
            )}
            {bulletPointsData && bulletPointsData.length > 0 && (
              <Stack
                gap={2.5}
                {...styles('bulletPointsContainer', {
                  ...(!actionButtonData && { marginTop: '2rem' }),
                })}
              >
                {bulletPointsData.map((item, i) => (
                  <Box key={i} {...styles('listItem')}>
                    <Icon
                      iconName="TickCircle"
                      colorValues={
                        !showGlow ? 'text.primary' : 'text.primaryDark'
                      }
                    />
                    <Typography
                      variant="body3.500"
                      color={!showGlow ? 'text.primary' : 'text.primaryDark'}
                    >
                      {item}
                    </Typography>
                  </Box>
                ))}
                <Box {...styles('listItem')}>
                  <Icon
                    iconName="TickCircle"
                    colorValues={
                      !showGlow ? 'text.primary' : 'text.primaryDark'
                    }
                  />
                  <Typography
                    variant="body3.500"
                    color={!showGlow ? 'text.primary' : 'text.primaryDark'}
                  >
                    {t('introsection.dicountPoints', {
                      discount:
                        dynamicPricingUSDAnnual?.projected_net_saving_rate ??
                        10,
                    })}
                  </Typography>
                </Box>
              </Stack>
            )}
            {/* {aiCreditData && (
              <AiCredit
                {...aiCreditData}
                creditAmount={dynamicPricingUSDAnnual?.credits ?? 10}
              />
            )} */}
          </Box>
        </Box>
        {leftViewChild}

        {actionButtonData && actionButtonData.length > 0 && (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            gap={2.5}
            display={{ xs: 'flex', sm: 'none' }}
          >
            {actionButtonData.map((item, i) => (
              <ActionButton
                {...item}
                key={i}
                showArrow
                iconSx={{ color: 'common.black' }}
                {...styles('actionButton', {
                  alignSelf: 'center',
                })}
              />
            ))}
          </Stack>
        )}
      </Box>
    </SectionWrapper>
  )
}
